import React from "react";
import styled from "styled-components";
import { style } from "../style";
import { useEvent } from "../hooks/useEvent";

type Props = {
  id: number;
  state: "complete" | "active" | "inactive";
  title: React.ReactNode;
  icon: React.ReactNode;

  onSelect?: (index: number) => void;
};

const Container = styled.button<{ $clickable: boolean }>`
  all: unset;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;

  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};

  &:focus-visible {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: 1px;
  }
`;

const IconWrapper = styled.div<{ $color: string }>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 2rem;
  height: 2rem;

  border-radius: 50%;

  border: ${style.vars.borderWidth.base} solid ${(props) => props.$color};

  font-weight: ${style.vars.fontWeight.bold};
  color: ${(props) => props.$color};
`;

const Text = styled.div<{ $color: string; $hoverColor: string; $clickable: boolean }>`
  font-size: ${style.vars.fontSize.base};
  font-weight: ${style.vars.fontWeight.bold};
  color: ${(props) => props.$color};

  &:hover {
    color: ${(props) => (props.$clickable ? props.$hoverColor : props.$color)};
  }
`;

const stateToIconColor: Record<Props["state"], string> = {
  complete: style.colors.green.dark,
  active: style.colors.blue.dark,
  inactive: style.colors.grey.primary,
};

const stateToTextColor: Record<Props["state"], string> = {
  complete: style.colors.grey.primary,
  active: style.colors.grey.darkest,
  inactive: style.colors.grey.primary,
};

const stateToTextHoverColor: Record<Props["state"], string> = {
  complete: style.colors.grey.darkest,
  active: style.colors.grey.darkest,
  inactive: style.colors.grey.primary,
};

export const WizardStep: React.FC<Props> = (props) => {
  const { state, icon, title } = props;

  const tabIndex = Number(state === "complete" && !!props.onSelect) - 1;
  const clickable = tabIndex === 0;

  const iconColor = stateToIconColor[state];
  const textColor = stateToTextColor[state];
  const textHoverColor = stateToTextHoverColor[state];

  const onSelect = useEvent<React.MouseEventHandler>((ev) => {
    ev.preventDefault();

    if (props.onSelect && clickable) {
      props.onSelect(props.id);
    }
  });

  return (
    <Container tabIndex={tabIndex} $clickable={clickable} onClick={onSelect}>
      <IconWrapper $color={iconColor}>{icon}</IconWrapper>
      <Text $color={textColor} $hoverColor={textHoverColor} $clickable={clickable}>
        {title}
      </Text>
    </Container>
  );
};
